import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { groupBy as ramdaGroupBy } from 'ramda';
import { getTripById, selectCar } from '../../redux/store/userData/cars';
import { selectUser } from '../../redux/store/userData/users';
import {
  fetchBookingById,
  selectBooking,
  selectTrip,
  selectSelectedBooking,
  selectSelectedTrip,
} from '../../redux/store/userData/trips';
import {
  selectCarWithoutMapEffect,
  selectDisplayColumnsForTimeTable,
} from '../../redux/store/ui/common';
import {
  COLLAPSE_MS,
  COLORS,
  DAYS_AFTER_TODAY,
  DAYS_BEFORE_FOCUS,
  DAYS_BEFORE_TODAY,
  HEADER_HEIGHT,
  PLATE_COL_WIDTH,
  ROW_HEIGHT,
  TIMELINE_COL_WIDTH,
} from './constants';
import Header from './components/Header';
import PlateColumn from './components/PlateColumn';
import useTimelineViewModel from './hooks/useTimelineViewModel';
import useFocusScroll from './hooks/useFocusScroll';
import useTimelineData from './hooks/useTimelineData';
import useBookingCanvas from './hooks/useBookingCanvas';
import useDateLocale from './hooks/useDateLocale';
import {
  Booking,
  buildTilesByCar,
  isBookingStatus,
  Tile,
} from './utils/bookingTiles';
import {
  GroupBy,
  getGroupKeyForCar,
  getGroupName,
  NO_GROUP_KEY,
} from './utils/grouping';
import {
  ColumnCtx,
  SortDirection,
  SortKey,
  TimelineSort,
  compareSortValues,
  getColumnSortValue,
  orderedExtraColumns,
} from './utils/timelineColumns';
import { buildStatusPeriodsByCar } from './utils/statusPeriods';
import { ZoomLevel, columnPosition, startOfDayMs } from './utils/timeScale';
import ColorLegend, {
  LegendCategory,
  LegendFilters,
} from './components/ColorLegend';
import { CarLike, RowEntry } from './types';

// Status priority for sorting — RELEASED first per product request, then the
// rest in lifecycle order, with unknown/missing statuses last.
const STATUS_ORDER: Record<string, number> = {
  RELEASED: 0,
  BLOCKED: 1,
  RENTED: 2,
  RELEASE_READY: 3,
  EQUIPPED: 4,
  EQUIPMENT_READY: 5,
  TERMINATED: 6,
  REMOVED: 7,
};

// Order of the STATUS *groups* when grouping by status (product request — distinct
// from the row sort order above): rented → released → blocked → release-ready, then
// any other statuses (alphabetical) after.
const STATUS_GROUP_ORDER: Record<string, number> = {
  RENTED: 0,
  RELEASED: 1,
  BLOCKED: 2,
  RELEASE_READY: 3,
};

const compareCarsByStatus = (a: CarLike, b: CarLike): number => {
  const ra = STATUS_ORDER[a.status || ''] ?? 99;
  const rb = STATUS_ORDER[b.status || ''] ?? 99;
  if (ra !== rb) return ra - rb;
  return (a.plate || '').localeCompare(b.plate || '');
};

const startOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const addDays = (d: Date, days: number): Date => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};

const VehicleTimeline: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const locale = useDateLocale();
  // Single centralized data loader (cars + areas/fleets/categories + bookings + status changes).
  const {
    cars,
    areas,
    fleets,
    categories,
    loading,
    bookings,
    statusChanges,
    tripBookings,
  } = useTimelineData();
  // Open booking/trip drawer → its id, so the canvas can grey out the other tiles.
  const selectedBooking = useSelector(selectSelectedBooking);
  const selectedTrip = useSelector(selectSelectedTrip);
  const selectedTileId: string | null =
    selectedBooking?.id ?? selectedTrip?.id ?? null;

  // Left columns chosen in the Columns selector (separate persisted `timeTable`
  // slot — never affects CarsTable). Plate is always shown; these render to its
  // right and widen the sticky-left region (leftWidth) accordingly.
  const selectedColumns = useSelector(selectDisplayColumnsForTimeTable);
  const extraColumns = useMemo(
    () => orderedExtraColumns(selectedColumns),
    [selectedColumns]
  );

  // Per-session left-column widths (drag the header's right edge to resize). Only
  // dragged columns get an override; everything else falls back to its default. The
  // ColumnResizer clamps to the per-column min / MAX_COL_WIDTH, so we store as-is.
  const [colWidthOverrides, setColWidthOverrides] = useState<
    Record<string, number>
  >({});
  const widthOf = useCallback(
    (key: string) =>
      colWidthOverrides[key] ??
      (key === 'plate' ? PLATE_COL_WIDTH : TIMELINE_COL_WIDTH),
    [colWidthOverrides]
  );
  const columnWidths = useMemo(() => {
    const map: Record<string, number> = { plate: widthOf('plate') };
    extraColumns.forEach((k) => {
      map[k] = widthOf(k);
    });
    return map;
  }, [widthOf, extraColumns]);
  const handleColumnResize = useCallback((key: string, width: number) => {
    setColWidthOverrides((prev) =>
      prev[key] === width ? prev : { ...prev, [key]: width }
    );
  }, []);

  // "Where the day area starts" — the sum of the sticky-left column widths. Threaded
  // to the canvas/header/today-line/legend, all of which key off this single value.
  const leftWidth =
    columnWidths.plate +
    extraColumns.reduce((sum, k) => sum + columnWidths[k], 0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // carId -> live body row element, read by the canvas for tile Y / collapse sync.
  const rowElRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  // Zoom level: 'monthly' (day columns) or 'daily' (6-hour columns). Per-session.
  const [zoom, setZoom] = useState<ZoomLevel>('daily');
  // Active grouping dimension (single-select). 'none' = flat list.
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  // Table-style sort over the left columns. null → default status order. Per-session.
  const [sort, setSort] = useState<TimelineSort>(null);
  // Legend doubles as a filter — which categories are drawn on the canvas. Per-session.
  const [legendFilters, setLegendFilters] = useState<LegendFilters>({
    booked: true,
    rental: true,
    releaseReady: true,
    blocked: true,
  });
  const toggleLegendFilter = useCallback((category: LegendCategory) => {
    setLegendFilters((prev) => ({ ...prev, [category]: !prev[category] }));
  }, []);
  // Keys (bucket key or NO_GROUP_KEY) of groups that are currently collapsed.
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());

  // Click a header arrow: sort that column asc/desc; clicking the already-active
  // arrow again clears the sort (back to the default status order).
  const handleSort = useCallback((key: SortKey, direction: SortDirection) => {
    setSort((prev) =>
      prev && prev.key === key && prev.direction === direction
        ? null
        : { key, direction }
    );
  }, []);

  const toggleGroup = (key: string) =>
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Switching dimension invalidates the old bucket keys — start fully expanded.
  const handleChangeGroupBy = (next: GroupBy) => {
    setGroupBy(next);
    setCollapsedKeys(new Set());
  };

  const { startDate, endDate, nowMs } = useMemo(() => {
    const now = new Date();
    const today = startOfDay(now);
    return {
      startDate: addDays(today, -DAYS_BEFORE_TODAY),
      endDate: addDays(today, DAYS_AFTER_TODAY),
      // Wall-clock "now" (ms) — positions the today line, clamps the current status
      // band's right edge, and filters upcoming bookings. Computed once at mount.
      nowMs: now.getTime(),
    };
  }, []);

  // Count of UPCOMING (future-starting) rental bookings per car, for the optional
  // "# of coming bookings" column (also a sortable column). Excludes status-change
  // bookings (blocks/releases). Declared before `rows` so the sort comparator can read it.
  const comingBookingsByCar = useMemo(() => {
    const counts = new Map<string, number>();
    bookings.forEach((b) => {
      if (b.statusChange === true || !b.carId || !b.from) return;
      if (new Date(b.from).getTime() > nowMs) {
        counts.set(b.carId, (counts.get(b.carId) ?? 0) + 1);
      }
    });
    return counts;
  }, [bookings, nowMs]);

  // Build row list: flat cars, or bucketed by the active dimension with a header
  // row per group. Mirrors the ramda groupBy pattern used in ClaimTimeline.
  const rows: RowEntry[] = useMemo(() => {
    // EQUIPMENT_READY cars are excluded from the timeline (business rule).
    const visibleCars = cars.filter((c) => c.status !== 'EQUIPMENT_READY');
    const notAssignedLabel = intl.formatMessage({
      id: 'fleetadmin.timeline.notAssigned',
      defaultMessage: 'Not assigned',
    });
    // Row comparator: when a header sort is active, order by that column's value
    // (text or numeric) with a plate tiebreak; otherwise fall back to the default
    // status order (RELEASED first → lifecycle, see STATUS_ORDER). Applied in flat
    // mode AND inside each bucket when grouped, so grouped sorting is within-group.
    const sortCtx: ColumnCtx = {
      areas,
      fleets,
      intl,
      notAssignedLabel,
      comingBookingsByCar,
    };
    const comparator = sort
      ? (a: CarLike, b: CarLike): number => {
          const r = compareSortValues(
            getColumnSortValue(sort.key, a, sortCtx),
            getColumnSortValue(sort.key, b, sortCtx)
          );
          const tie =
            r !== 0 ? r : (a.plate || '').localeCompare(b.plate || '');
          return sort.direction === 'asc' ? tie : -tie;
        }
      : compareCarsByStatus;
    const sortedCars = [...visibleCars].sort(comparator);
    if (groupBy === 'none') {
      return sortedCars.map((car) => ({ kind: 'car' as const, car }));
    }
    const nameCtx = {
      areas,
      fleets,
      categories,
      intl,
      notAssignedLabel,
    };
    const buckets = ramdaGroupBy<CarLike>((c) => getGroupKeyForCar(c, groupBy))(
      sortedCars
    );
    const keys = Object.keys(buckets);
    // Real groups sorted by display name; the "Not assigned" bucket always last.
    // EXCEPT status grouping, which uses a fixed lifecycle order (STATUS_GROUP_ORDER),
    // falling back to name order for any status not in the map.
    const realKeys = keys
      .filter((k) => k !== NO_GROUP_KEY)
      .sort((a, b) => {
        if (groupBy === 'status') {
          const oa = STATUS_GROUP_ORDER[a] ?? 99;
          const ob = STATUS_GROUP_ORDER[b] ?? 99;
          if (oa !== ob) return oa - ob;
        }
        return getGroupName(groupBy, a, nameCtx).localeCompare(
          getGroupName(groupBy, b, nameCtx)
        );
      });
    const orderedKeys = keys.includes(NO_GROUP_KEY)
      ? [...realKeys, NO_GROUP_KEY]
      : realKeys;
    const out: RowEntry[] = [];
    orderedKeys.forEach((key) => {
      out.push({
        kind: 'group',
        key,
        name: getGroupName(groupBy, key, nameCtx),
        count: (buckets[key] || []).length,
      });
      // Cars stay in the list even when their group is collapsed — they animate
      // to height 0 rather than unmounting, so expand/collapse can be smooth.
      (buckets[key] || []).forEach((car) =>
        out.push({ kind: 'car', car, groupKey: key })
      );
    });
    return out;
  }, [
    cars,
    areas,
    fleets,
    categories,
    groupBy,
    intl,
    sort,
    comingBookingsByCar,
  ]);

  const isRowHidden = (row: RowEntry): boolean =>
    row.kind === 'car' && !!row.groupKey && collapsedKeys.has(row.groupKey);

  const vm = useTimelineViewModel({
    startDate,
    endDate,
    rowCount: rows.length || 1,
    zoom,
  });

  // End-date label: localized date + time (date-fns `P p` → e.g. "06/12/2026,
  // 2:30 PM" en / "12.06.2026 14:30" de). `to` keeps its time, so the label shows
  // both date and time even though tile X/width are day-granular.
  const formatEnd = useCallback(
    (date: Date) => {
      const localeCode = locale.code || 'en-US';
      // Hide the year when it's the current year (just clutter); keep it otherwise.
      const sameYear = date.getFullYear() === new Date().getFullYear();
      const opts: Intl.DateTimeFormatOptions = {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      };
      if (!sameYear) opts.year = 'numeric';
      return new Intl.DateTimeFormat(localeCode, opts).format(date);
    },
    [locale]
  );

  // Planned status changes (block / release-ready) arrive via active bookings flagged
  // `statusChange` — they render as background bands, not tiles. Split them out here.
  const statusChangeBookings = useMemo(
    () => bookings.filter((b) => b.statusChange === true),
    [bookings]
  );

  // Booking tiles per car, positioned on the active zoom's column scale. Active
  // bookings + past trips (finished = rental tiles, cancelled = grey) are merged
  // here only for tile building (coming-bookings / status bands stay on `bookings`).
  // buildTilesByCar internally skips statusChange bookings, so passing all is fine.
  const tileBookings = useMemo(
    () =>
      [...bookings, ...tripBookings].filter((b) =>
        // Legend filter: olive = bookings, lime = rentals/trips.
        isBookingStatus(b) ? legendFilters.booked : legendFilters.rental
      ),
    [bookings, tripBookings, legendFilters]
  );
  const tilesByCar = useMemo(
    () =>
      buildTilesByCar(
        tileBookings,
        vm.days,
        vm.columnsPerDay,
        vm.columnWidth,
        formatEnd
      ),
    [tileBookings, vm.days, vm.columnsPerDay, vm.columnWidth, formatEnd]
  );

  // Status period bands per car (grey RELEASE_READY / dashed-red BLOCKED), drawn
  // behind the tiles — merged from history (clamped to now) + planned bookings.
  const statusPeriodsByCar = useMemo(
    () =>
      buildStatusPeriodsByCar(
        statusChanges,
        statusChangeBookings,
        cars,
        vm.days,
        vm.columnsPerDay,
        vm.columnWidth
      ),
    [
      statusChanges,
      statusChangeBookings,
      cars,
      vm.days,
      vm.columnsPerDay,
      vm.columnWidth,
    ]
  );

  // Legend filter for the status bands: drop RELEASE_READY / BLOCKED bands when their
  // legend toggle is off.
  const filteredStatusPeriods = useMemo(() => {
    if (legendFilters.releaseReady && legendFilters.blocked) {
      return statusPeriodsByCar;
    }
    const out: typeof statusPeriodsByCar = new Map();
    statusPeriodsByCar.forEach((periods, carId) => {
      const kept = periods.filter((p) =>
        p.status === 'BLOCKED'
          ? legendFilters.blocked
          : legendFilters.releaseReady
      );
      if (kept.length) out.set(carId, kept);
    });
    return out;
  }, [statusPeriodsByCar, legendFilters]);

  // Shared per-car context handed to each optional column's getValue (see
  // utils/timelineColumns). Defined here so it can include comingBookingsByCar.
  const columnCtx: ColumnCtx = useMemo(
    () => ({
      areas,
      fleets,
      intl,
      notAssignedLabel: intl.formatMessage({
        id: 'fleetadmin.timeline.notAssigned',
        defaultMessage: 'Not assigned',
      }),
      comingBookingsByCar,
    }),
    [areas, fleets, intl, comingBookingsByCar]
  );

  // Content-space x of the current-time line (column-scale aware), or null off-range.
  const todayLineX =
    vm.todayIdx >= 0 && vm.days.length
      ? columnPosition(
          nowMs,
          startOfDayMs(vm.days[0].getTime()),
          vm.columnsPerDay
        ) * vm.columnWidth
      : null;

  // Click a tile → open the SAME drawer the BookingsTable uses (booking vs trip).
  // Only ONE drawer (booking / trip / vehicle) may be open at a time, so clear the
  // other two selections in the same batch — otherwise antd stacks them (nesting).
  const onTileClick = (tile: Tile) => {
    const b = tile.booking;
    if (tile.isBooking) {
      batch(() => {
        dispatch(selectTrip(null) as any);
        dispatch(selectCar(null) as any);
        dispatch(selectUser(null) as any);
        dispatch(selectBooking(b as any));
        dispatch(fetchBookingById(b.id) as any);
      });
    } else {
      batch(() => {
        dispatch(selectBooking(null) as any);
        dispatch(selectCar(null) as any);
        dispatch(selectUser(null) as any);
        dispatch(getTripById(b.id) as any);
      });
    }
  };

  // Click a PLANNED status-change band (block / release-ready) → it's a booking, so
  // open the booking drawer just like a booking tile.
  const onBandClick = (booking: Booking) => {
    batch(() => {
      dispatch(selectTrip(null) as any);
      dispatch(selectCar(null) as any);
      dispatch(selectUser(null) as any);
      dispatch(selectBooking(booking as any));
      dispatch(fetchBookingById(booking.id) as any);
    });
  };

  // Click a plate → open that vehicle's drawer (same flow as CarTable's row click).
  // Clear any open booking/trip/user drawer first so only one drawer is open (a
  // lingering selected user would otherwise re-open over the next drawer).
  const onPlateClick = (car: CarLike) => {
    batch(() => {
      dispatch(selectBooking(null) as any);
      dispatch(selectTrip(null) as any);
      dispatch(selectUser(null) as any);
      dispatch(selectCarWithoutMapEffect(car as any) as any);
    });
  };

  // Hover tooltip showing the FULL booking code (the canvas label is ellipsised on
  // small tiles). Deduped by label so it only re-renders on tile enter/leave — NOT
  // on every mousemove — keeping the heavy timeline tree from re-rendering constantly.
  const [hoverTip, setHoverTip] = useState<{
    label: string;
    x: number;
    y: number;
  } | null>(null);
  const handleHoverChange = useCallback(
    (info: { label: string; x: number; y: number } | null) => {
      setHoverTip((prev) => {
        if (!info) return null;
        if (prev && prev.label === info.label) return prev; // same tile → no re-render
        return info;
      });
    },
    []
  );

  // Signature that flips on any collapse/expand (or regroup) → kicks the canvas
  // animation loop so tiles re-track row geometry.
  const collapseSignature = useMemo(
    () => `${groupBy}|${Array.from(collapsedKeys).sort().join(',')}`,
    [groupBy, collapsedKeys]
  );

  // Signature of the rendered row ORDER (cars + group headers). Flips on sort, group,
  // or data changes so the canvas redraws tiles against the new row geometry — without
  // it a sort only reorders the DOM rows and the tiles lag until the next scroll.
  const rowOrderSignature = useMemo(
    () =>
      rows.map((r) => (r.kind === 'car' ? r.car.id : `g:${r.key}`)).join('|'),
    [rows]
  );

  useBookingCanvas({
    canvasRef,
    scrollRef,
    tilesByCar,
    statusPeriodsByCar: filteredStatusPeriods,
    rowElRefs,
    collapseSignature,
    rowOrderSignature,
    selectedId: selectedTileId,
    leftWidth,
    columnWidth: vm.columnWidth,
    todayLineX,
    onTileClick,
    onBandClick,
    onHoverChange: handleHoverChange,
  });

  useFocusScroll(scrollRef, vm);

  // Scroll today into view on first mount AND whenever the zoom changes (the column
  // scale changes, so re-center to keep the view oriented). Same formula as
  // useFocusScroll — yesterday sits at the day-area's left edge. `dayWidth` = the
  // width of ONE day at the active zoom (columnsPerDay × columnWidth).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (vm.todayIdx < 0) return;
    const dayWidth = vm.columnsPerDay * vm.columnWidth;
    const left = (vm.todayIdx - DAYS_BEFORE_FOCUS) * dayWidth;
    el.scrollLeft = Math.max(0, left);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom]);

  // (The current-time line is now drawn ON the canvas — over the status bands,
  // under the tiles — and the body-region clip hides it behind the left columns,
  // so the old DOM accent line + its scroll-visibility effect are gone.)

  const showEmptyState = !loading && cars.length === 0;

  return (
    <div
      style={{
        // Positioning context for the floating ColorLegend. Carries the viewport
        // clamp (see below); the inner scroll container fills it.
        position: 'relative',
        width: '100%',
        height: '100%',
        maxHeight: 'calc(100vh - 132px)',
      }}
    >
      <div
        ref={scrollRef}
        style={{
          position: 'relative',
          width: '100%',
          // `.main-body` sits at viewport y=8 with 116px top padding, plus an 8px
          // AppliedFilters bar above us, so our scroll wrap starts at viewport y=132.
          // The wrapper above carries `maxHeight: calc(100vh - 132px)`, so filling it
          // (height:100%) keeps this from overshooting past the window — the
          // `.main-body` overflow landmine — while overflow:auto scrolls.
          height: '100%',
          overflow: 'auto',
          background: '#FFFFFF',
        }}
      >
        {/* Booking-tile canvas — FIRST child so `position: sticky; top/left:0` keeps
          it pinned to the scroll viewport from the start (a last child never pins —
          sticky only holds an element you scroll PAST). Negative margins collapse
          its flow box so it doesn't push the header down / add scroll space.
          z:1 paints it above the body content; header (z:3) + plate column (z:2)
          stay above it. pointer-events:none → the scroll container catches clicks
          (hit-tested in useBookingCanvas). */}
        <canvas
          ref={canvasRef}
          style={{
            // `block` is required: a default-`inline` canvas sits on the text
            // baseline, adding a line-box leading gap ABOVE it (it's the first
            // flow child) that pushes the header down. block removes that gap.
            display: 'block',
            position: 'sticky',
            top: 0,
            left: 0,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        />

        <Header
          vm={vm}
          scrollRef={scrollRef}
          groupBy={groupBy}
          onChangeGroupBy={handleChangeGroupBy}
          zoom={zoom}
          onChangeZoom={setZoom}
          leftWidth={leftWidth}
          extraColumns={extraColumns}
          columnWidths={columnWidths}
          onColumnResize={handleColumnResize}
          sort={sort}
          onSort={handleSort}
        />

        <div
          style={{
            position: 'relative',
            display: 'flex',
            width: leftWidth + vm.totalWidth,
          }}
        >
          <PlateColumn
            rows={rows}
            collapsedKeys={collapsedKeys}
            onToggleGroup={toggleGroup}
            onPlateClick={onPlateClick}
            groupBy={groupBy}
            leftWidth={leftWidth}
            extraColumns={extraColumns}
            columnWidths={columnWidths}
            columnCtx={columnCtx}
          />

          {/* Body — per-row divs so rows can animate to height 0 on collapse in
            lockstep with the plate cells. Vertical gridlines are a full-height
            gradient on the container (unaffected by row heights); horizontal
            gridlines are per-row borderTop. Body height is intrinsic (sum of the
            animating row heights) so the container + today line shrink smoothly. */}
          <div
            style={{
              position: 'relative',
              width: vm.totalWidth,
              backgroundColor: '#F8FAFC',
              backgroundImage: `linear-gradient(to right, ${COLORS.gridLineVertical} 1px, transparent 1px)`,
              backgroundSize: `${vm.columnWidth}px 100%`,
            }}
          >
            {rows.map((row, i) => {
              if (row.kind === 'group') {
                return (
                  <div
                    key={`body-group-${row.key}-${i}`}
                    style={{
                      height: ROW_HEIGHT,
                      background: COLORS.groupRowBg,
                      borderTop: `1px solid ${COLORS.gridLineHorizontal}`,
                      boxSizing: 'border-box',
                    }}
                  />
                );
              }
              const hidden = isRowHidden(row);
              return (
                <div
                  key={`body-car-${row.car.id}`}
                  ref={(el) => {
                    rowElRefs.current.set(row.car.id, el);
                  }}
                  style={{
                    height: hidden ? 0 : ROW_HEIGHT,
                    opacity: hidden ? 0 : 1,
                    borderTop: hidden
                      ? '0 solid transparent'
                      : `1px solid ${COLORS.gridLineHorizontal}`,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    transition: `height ${COLLAPSE_MS}ms ease`,
                  }}
                />
              );
            })}

            {/* Today/current-time line is drawn on the canvas (see useBookingCanvas),
              over the status bands and under the tiles. */}

            {showEmptyState && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: ROW_HEIGHT * 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#94A3B8',
                  fontSize: 14,
                  pointerEvents: 'none',
                }}
              >
                No vehicles
              </div>
            )}
          </div>
        </div>

        {loading && cars.length === 0 && (
          <div
            style={{
              position: 'absolute',
              top: HEADER_HEIGHT,
              left: leftWidth,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#94A3B8',
              fontSize: 14,
              pointerEvents: 'none',
            }}
          >
            Loading…
          </div>
        )}
      </div>

      {/* Floating colour key (swatch + meaning), pinned bottom-right — capped by
          leftWidth so it never overlaps the sticky left column(s). */}
      {!showEmptyState && (
        <ColorLegend
          leftWidth={leftWidth}
          filters={legendFilters}
          onToggle={toggleLegendFilter}
        />
      )}

      {/* Hover tooltip — full booking code for tiles whose label is ellipsised. */}
      {hoverTip && (
        <div
          style={{
            position: 'fixed',
            left: hoverTip.x + 12,
            top: hoverTip.y - 30,
            zIndex: 2000,
            pointerEvents: 'none',
            background: 'rgba(15, 23, 42, 0.92)',
            color: '#FFFFFF',
            fontSize: 12,
            fontWeight: 600,
            padding: '3px 8px',
            borderRadius: 6,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
          }}
        >
          {hoverTip.label}
        </div>
      )}
    </div>
  );
};

export default VehicleTimeline;
